import { handlePayment } from '@api/payment';
import { TCharge, TOrder, TOrderItem } from '@components/types';
import { getAddress, sanityIoImageLoader } from '@core/utils';
import { Check, Clock, Phone, X } from 'lucide-react';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { PrintOrderComponent } from '../printorder';

type Props = {
    currentOrders?: TOrder[]
    transactionId?: string
    storeId?: number
}



const DetailOrder: React.FC<Props> = ({ currentOrders, transactionId, storeId }) => {
    const router = useRouter()
    const componentRef = useRef();


    const retryOrder = async (order: TOrder) => {
        if (transactionId?.length > 0) {
            const paymentResponse = handlePayment(
                transactionId, `/shop/${storeId}?transaction_id=${transactionId}&mode=status`
            );
            paymentResponse
                .then((response) => {
                    router.push(response.data.url);
                })
                .catch((error) => {
                    console.debug(error);
                });
        }
        else {
            const paymentResponse = handlePayment(
                currentOrders[0]?.transaction_id, `/shop/${currentOrders[0]?.store?.id}?transaction_id=${currentOrders[0]?.transaction_id}&mode=status`
            );
            paymentResponse
                .then((response) => {
                    router.push(response.data.url);
                })
                .catch((error) => {
                    console.debug(error);
                });
        }
    };

    const handlePrint = () => {
        handleReactPrint()
    }

    const handleReactPrint = useReactToPrint({
        content: () => componentRef.current,
    });

    return (
        <div className="flex flex-col justify-between grow gap-2 w-full overflow-y-scroll px-2 font-manrope">
            <div className="flex flex-col gap-4 px-4">
                {currentOrders?.map((order: TOrder) => {
                    return (<div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row justify-between gap-1 items-center">
                                <div className="flex gap-1 items-center">
                                    {order?.is_paid ? <Check size={20} className="text-success-300" /> : <X size={20} className="text-error-300" />}
                                    <span className={`text-base font-bold ${order?.is_paid ? "text-success-300" : "text-error-300"}`}>
                                        {order?.is_paid ? "Payment Successful" : "Payment Due"}
                                    </span>
                                    <span className="text-base text-white font-semibold">&nbsp;
                                        {!order?.is_paid && `for ₹${order?.due}`}
                                    </span>
                                </div>
                                <span className="text-base text-white font-extrabold">
                                    #{order?.short_id}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <h1 className="text-base text-white font-extrabold">
                                    {order?.store?.name}
                                </h1>
                                {order?.store?.phone && (
                                    <a href={`tel:${order?.store?.phone}`}>
                                        <button
                                            type="button"
                                            className="h-6 px-4 tracking-wider flex items-center gap-1 transition transition-all duration-200 rounded-md bg-brand-500 hover:opacity-80 cursor-pointer"
                                        >
                                            <span className="text-sm text-white font-manrope font-semibold">
                                                CALL
                                            </span>
                                            <Phone className="w-3 h-3 text-white" />
                                        </button>
                                    </a>
                                )}
                            </div>
                            <p className="text-sm text-white font-semibold">
                                {getAddress(order?.store?.address)}
                            </p>
                            <span className="text-xs text-white font-medium">
                                {order?.updated_on != null ? `${moment(order?.updated_on).format("MMMM DD, YYYY | h:mm A")}` : moment(order?.added_on).format("MMMM DD, YYYY | h:mm A")}
                            </span>
                            {(order?.info?.delivery_date && order?.status !== "Completed" && order?.status !== "Cancelled") && <div className='text-success-300 text-sm flex items-center gap-1'><Clock size={12} /> Delivery On: <span className='font-semibold'>{moment(order?.info?.delivery_date).format("DD MMM, h:mm A")}</span></div>}
                        </div>
                        <div className="mt-2 py-4 flex flex-col gap-2.5 border border-t-none border-x-none border-solid border-neutral-200">
                            <h6 className="text-base text-white font-extrabold">
                                Order Details
                            </h6>
                            <div className="flex flex-col gap-2.5">
                                {order?.orderitems?.map((item: TOrderItem) => (
                                    <div
                                        key={item?.id}
                                        className="flex justify-between items-center"
                                    >
                                        <div className="flex items-start gap-2.5">
                                            {item?.inventory?.product?.images.length > 0 && (
                                                <Image
                                                    src={item?.inventory?.product?.images[0].url}
                                                    alt={item?.inventory?.product?.images[0].description || item?.inventory?.product?.name}
                                                    loader={sanityIoImageLoader}
                                                    width={40}
                                                    height={40}
                                                    className="object-cover rounded-sm"
                                                />
                                            )}
                                            <div className="flex flex-col">
                                                <div className="flex gap-2">
                                                    <h4 className="text-sm text-white font-semibold">
                                                        {item?.inventory?.product?.name}
                                                    </h4>

                                                    <span className="text-sm text-white font-manrope font-medium">
                                                        x{item?.quantity}
                                                    </span>
                                                </div>
                                                {
                                                    item?.add_ons?.length > 0 &&
                                                    <div className="flex flex-row text-sm gap-1 text-white">
                                                        <span className="font-semibold">Addon :</span>
                                                        {item?.add_ons?.map((x) => x?.inventory?.product?.name)?.filter((x) => x != undefined).join(", ")}
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                        <span className="text-sm text-white font-bold">
                                            ₹{item?.total_cost?.toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="py-4 flex flex-col gap-2 border border-t-none border-x-none border-solid border-neutral-200">
                            {order?.charges?.map((item: TCharge) => (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-white font-semibold">{item?.charge?.display_name}</span>
                                    <span className="text-sm text-white font-bold">₹{item?.total_cost?.toFixed(2)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-white font-semibold">GST</span>
                                <span className="text-sm text-white font-bold">₹{order?.tax?.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="py-4 flex flex-col gap-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-white font-bold">Total</span>
                                <span className="text-sm text-white font-extrabold">₹{Math.round(order?.payable)?.toFixed(2)}</span>
                            </div>
                        </div>
                        <div className="mx-auto max-w-[1240px] fixed bottom-16 left-0 right-0 flex items-center gap-2">
                            {
                                order?.is_paid ?
                                    <div onClick={() => { handlePrint() }} className="flex-1 px-2 flex cursor-pointer"                             >
                                        <button type="button" className="flex justify-center items-center w-full h-10 rounded-md text-sm text-white font-manrope font-medium bg-brand-500 tracking-wide">
                                            DOWNLOAD BILL
                                        </button>
                                    </div>
                                    :

                                    <div className="flex-1 px-2 flex cursor-pointer"                                                    >
                                        <button onClick={() => { retryOrder(order) }} type="button" className="flex justify-center items-center w-full h-10 rounded-md text-sm text-white font-manrope font-medium bg-brand-500 tracking-wide">
                                            RETRY PAYMENT
                                        </button>
                                    </div>
                            }
                        </div>
                        <div className="hidden">
                            <div ref={componentRef} >
                                <PrintOrderComponent order={order} ref={componentRef} />
                            </div>
                        </div>
                    </div>)
                })}
            </div>

        </div>
    )
}

export default DetailOrder